import { useContext, useEffect, useState, useRef } from "react";
import {
  ParagraphText,
  UBAppModal,
  UBButton,
  UBOTPInput,
} from "../../../../Libraries";
import { LanguageContext } from "../../../../context/LanguageContext";
import { Box, CircularProgress } from "@mui/material";
import { useSnackbar } from "../../../../context/SnackbarContext";
import { useNavigate, useLocation } from "react-router-dom";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import { useWalletContext } from "../../../../context/WalletContext";
import { useAuthContext } from "../../../../context/AuthContext";
import { handleApi } from "../../../../apis";
import {
  ACCOUNT_PIN_RESET,
  CHECK_PIN_GET,
  GETWALLETSENTMONEY,
  WALLETSENTMONEY,
} from "../../../../apis/configuration";
import Cookies from "js-cookie";
import modalStyles from "./VerifyPinModal.module.css";
import BusinessLoader from "../../../common/BusinessLoader";
import {
  calculateTotalOutstanding,
  findCustomerByID,
} from "../../../../database/helpers/customer";
import {
  bulkCreateOrdersV3,
  bulkDeleteOrders,
  bulkRestoreStock,
  bulkUpdateOrders,
  bulkUpdateTransactionStock,
} from "../../../../database/helpers/orders";
import { bulkUpdateStockV2 } from "../../../../database/helpers/stock";
import {
  bulkCreateInventoryV3,
  bulkDeleteInventory,
  bulkUpdateInventory,
} from "../../../../database/helpers/inventory";
import {
  bulkCreatePayments,
  getPaymentsByInvoiceExternal,
} from "../../../../database/helpers/payment";
import { getInvoiceByID } from "../../../../database/helpers/invoice";
import { getConvertedDisountMehod } from "../../../Features/NewInvoiceBook/invoiceBookUtils";
import { formattedCount } from "../../../../utils";
import { formatNum, uuid } from "../../../../oscar-pos-core/constants";
import { Sync } from "../../../../SyncService";

const VerifyPinModal = ({
  open,
  handleClose,
  amount = 0,
  accountDetail = {},
  expensePayload = null,
  invoiceUpdatePayload = null,
  backPath = null,
  categoryId = "",
  notes = "",
  idempotencyKey = "",
}) => {
  const [I18n] = useContext(LanguageContext);
  const [error, setError] = useState(false);
  const [PIN, setPIN] = useState("");
  const [loading, setLoading] = useState(false);
  const isDisabled = !(PIN.length === 4) || loading;
  const { showSnackbar } = useSnackbar();
  const { fetchWalletData } = useWalletContext();
  const { business, user, handleLogoutInDB } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const onChange = (val) => setPIN(val);

  const otpContainerRef = useRef(null);

  const focusFirstInput = () => {
    setTimeout(() => {
      const el = otpContainerRef.current?.querySelector(
        "input:not([disabled])",
      );
      if (el) el.focus();
    }, 100);
  };

  const openRef = useRef(open);
  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const handleForgotPIN = async () => {
    const phone = localStorage.getItem("phone_number");
    try {
      setLoading(true);
      triggerLogEvent(analytics, "w_send_forgot_pin");
      const response = await handleApi(
        ACCOUNT_PIN_RESET,
        { phone_number: phone },
        null,
        navigate,
        { "Api-Key": process.env.REACT_APP_API_KEY },
      );
      const isVerified = response?.verified;
      if (Boolean(isVerified)) {
        sessionStorage.setItem(
          "forgotPinVerify",
          JSON.stringify({ phone: phone || "" }),
        );
      }
      await handleLogoutInDB(navigate);
      if (Boolean(isVerified))
        navigate("/", {
          replace: true,
          state: { isVerifyAccountScreen: true, phoneNumber: phone },
        });
      else {
        navigate("/ForgotPin", { state: { phone } });
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const createExpenseTransaction = async () => {
    if (!expensePayload || !business) return;
    const cust = expensePayload.customerId
      ? await findCustomerByID(expensePayload.customerId)
      : null;

    if (expensePayload.source === "cash_book") {
      const amount = Number(expensePayload.amount) || 0;
      const cashTxnNextCount = Number(business.invoice_number_count || 0) + 1;
      await business.createCashTxn({
        amount,
        sales_amount: 0,
        cost_amount: amount,
        total: amount,
        sub_total: amount,
        discount: Number(expensePayload.discount) || 0,
        discount_unit: getConvertedDisountMehod(expensePayload.discountUnit),
        tax_title: "",
        tax_rate: Number(expensePayload.taxRate) || 0,
        advance_amount: 0,
        invoice_title: "",
        invoice_number: `${formattedCount(cashTxnNextCount)}`,
        invoice_prefix: "OUT#",
        signature: null,
        profit: 0,
        attachment: expensePayload.files,
        date: expensePayload.date ? new Date(expensePayload.date) : new Date(),
        image_url: "",
        document_url: "",
        note: expensePayload.note,
        status: "paid",
        tranType: "cash",
        method: "bank",
        type: "out",
        bank: "",
        wallet: "",
        category: expensePayload.category,
        customer: cust,
      });
      await business.updateBusinessInvoiceNo(cashTxnNextCount);
      Sync(user?.phone_number, navigate);
      return;
    }

    const isPO = Boolean(expensePayload.isPurchaseOrder);
    const nextCount = Number(business.invoice_number_count || 0) + 1;
    const invoiceData = {
      id: uuid(),
      amount: expensePayload.amount,
      sales_amount: 0,
      cost_amount: expensePayload.amount,
      date: new Date(expensePayload.date),
      customer: cust,
      image_url: "",
      document_url: "",
      signature: null,
      note: expensePayload.note,
      reference_id: expensePayload.reference_id || "",
      status: "paid",
      method: "bank",
      category: expensePayload.category,
      type: "out",
      tranType: isPO ? "bill" : "cash",
      bank: "",
      wallet: "",
      invoice_title: "",
      invoice_number: `${formattedCount(nextCount)}`,
      invoice_prefix: isPO ? "PO#" : "EXP#",
      due_date: expensePayload.due_date || null,
      discount: expensePayload.discount,
      discount_unit: getConvertedDisountMehod(expensePayload.discountUnit),
      tax_rate: expensePayload.taxRate,
      total: expensePayload.amount,
      sub_total: expensePayload.subTotal,
      advance_amount: expensePayload.amount,
      profit: 0,
      partialAmount: 0,
      attachment: expensePayload.files,
    };
    const invoice = await business.createInvoice(invoiceData);
    if (expensePayload.items?.length) {
      const ordersByItemId = await bulkCreateOrdersV3(
        business,
        invoice,
        expensePayload.items,
        "out",
        "quick_expense",
      );
      await bulkUpdateStockV2(expensePayload.items, "out");
      await bulkCreateInventoryV3(
        business,
        invoice,
        expensePayload.items,
        "in",
        "cash",
        ordersByItemId,
      );
    }
    if (isPO) {
      await business.updateBusinessInvoiceNo(nextCount);
      if (cust && invoice) {
        const payObj = {
          date: invoiceData.date,
          udhaar_note: invoiceData.note || "",
          imageURL: "",
          type: "invoicebook",
        };
        await bulkCreatePayments(business, cust, invoice, [
          {
            ...payObj,
            uid: uuid(),
            amount: invoiceData.amount,
            payment_mode: "Jama",
            external_type: "full_invoice",
          },
          {
            ...payObj,
            uid: uuid(),
            amount: invoiceData.amount,
            payment_mode: "Udhaar",
            external_type: "partial_invoice",
          },
        ]);
        const totalOutstanding = await calculateTotalOutstanding(cust);
        await cust.updateTotalOutstanding(totalOutstanding);
      }
    } else {
      await business.updateBusinessInvoiceNo(nextCount);
    }
    Sync(user?.phone_number, navigate);
  };

  const updateExistingExpense = async () => {
    if (!invoiceUpdatePayload || !business) return;
    const {
      invoiceId,
      customerId,
      mode,
      currentAdvanceAmount,
      previousAmount,
      addedCustomer: hadCustomer,
    } = invoiceUpdatePayload;
    const isEditForm = mode === "edit_form";
    const expense = await getInvoiceByID(invoiceId);
    const baseAdvance = isEditForm
      ? Number(currentAdvanceAmount || 0)
      : Number(expense.advance_amount || 0);
    const newAdvanceAmount = baseAdvance + Number(amount);
    const invoiceAmount = isEditForm
      ? invoiceUpdatePayload.amount
      : expense.amount;
    const invoiceStatus =
      invoiceAmount - newAdvanceAmount <= 0 ? "paid" : "unpaid";
    const cust = customerId ? await findCustomerByID(customerId) : null;

    const dataToEdit = {
      customer: cust,
      recording_uri: isEditForm
        ? invoiceUpdatePayload.recording_uri
        : expense.recording || null,
      advance_amount: newAdvanceAmount,
      invoice_number: isEditForm
        ? invoiceUpdatePayload.invoice_number
        : expense.invoice_number,
      invoice_prefix: isEditForm
        ? invoiceUpdatePayload.invoice_prefix
        : expense.invoice_prefix,
      invoice_title: isEditForm
        ? invoiceUpdatePayload.invoice_title
        : expense.invoice_title,
      discount_unit: isEditForm
        ? invoiceUpdatePayload.discount_unit
        : expense.discount_unit,
      sales_amount: isEditForm
        ? invoiceUpdatePayload.sales_amount
        : expense.sales_amount,
      document_url: isEditForm
        ? invoiceUpdatePayload.document_url
        : expense.document_url,
      cost_amount: isEditForm
        ? invoiceUpdatePayload.cost_amount
        : expense.cost_amount,
      attachment: isEditForm
        ? invoiceUpdatePayload.attachment
        : expense.attachment,
      sub_total: isEditForm
        ? invoiceUpdatePayload.sub_total
        : expense.sub_total,
      signature: isEditForm
        ? invoiceUpdatePayload.signature
        : expense.signature,
      tax_title: isEditForm
        ? invoiceUpdatePayload.tax_title
        : expense.tax_title,
      image_url: isEditForm
        ? invoiceUpdatePayload.image_url
        : expense.image_url,
      date: new Date(),
      due_date: isEditForm
        ? invoiceUpdatePayload.due_date || null
        : expense.due_date || null,
      category: isEditForm ? invoiceUpdatePayload.category : expense.category,
      discount: isEditForm ? invoiceUpdatePayload.discount : expense.discount,
      tax_rate: isEditForm ? invoiceUpdatePayload.tax_rate : expense.tax_rate,
      amount: invoiceAmount,
      method: "bank",
      wallet: null,
      profit: isEditForm ? 0 : expense.profit,
      status: invoiceStatus,
      total: isEditForm ? invoiceUpdatePayload.total || 0 : expense.total || 0,
      note: isEditForm ? invoiceUpdatePayload.note : expense.note,
      reference_id: isEditForm
        ? invoiceUpdatePayload.reference_id || ""
        : expense.reference_id || "",
      bank: null,
      partialAmount: invoiceAmount - newAdvanceAmount,
    };

    if (cust) {
      const dataForPayment = {
        udhaar_note: dataToEdit.note,
        date: dataToEdit.date,
        image_url: dataToEdit.image_url || null,
        invoiceDetail: expense,
        type: invoiceUpdatePayload.invoiceType || "cashbook",
      };

      if (isEditForm && !hadCustomer) {
        // New customer being added for the first time — create both payment entries
        const paymentArr = [
          {
            ...dataForPayment,
            uid: uuid(),
            amount: invoiceAmount,
            payment_mode: "Jama",
            external_type: "full_invoice",
          },
        ];
        if (newAdvanceAmount > 0) {
          paymentArr.push({
            ...dataForPayment,
            uid: uuid(),
            amount: newAdvanceAmount,
            payment_mode: "Udhaar",
            external_type: "partial_invoice",
          });
        }
        await bulkCreatePayments(business, cust, expense, paymentArr);
      } else {
        // Customer already existed — update payment records
        if (isEditForm && invoiceAmount !== previousAmount) {
          const fullPayments = await getPaymentsByInvoiceExternal(
            invoiceId,
            "full_invoice",
          );
          if (fullPayments.length) {
            await fullPayments[0].updatePayment({
              ...dataForPayment,
              amount: invoiceAmount,
            });
          } else {
            await bulkCreatePayments(business, cust, expense, [
              {
                ...dataForPayment,
                uid: uuid(),
                amount: invoiceAmount,
                payment_mode: "Jama",
                external_type: "full_invoice",
              },
            ]);
          }
        }
        if (baseAdvance !== newAdvanceAmount) {
          const partialPayments = await getPaymentsByInvoiceExternal(
            invoiceId,
            "partial_invoice",
          );
          if (partialPayments.length) {
            await partialPayments[0].updatePayment({
              ...dataForPayment,
              amount: newAdvanceAmount,
            });
          } else {
            await bulkCreatePayments(business, cust, expense, [
              {
                ...dataForPayment,
                uid: uuid(),
                amount: newAdvanceAmount,
                payment_mode: "Udhaar",
                external_type: "partial_invoice",
              },
            ]);
          }
        }
      }
      const totalOutstanding = await calculateTotalOutstanding(cust);
      await cust.updateTotalOutstanding(totalOutstanding);
    }
    await expense.updateInvocie(dataToEdit);

    if (isEditForm && invoiceUpdatePayload.items?.length >= 0) {
      const selectedItems = invoiceUpdatePayload.items || [];
      const existingOrders = await expense.orders.fetch();
      const orderMap = {};
      existingOrders.forEach((o) => {
        orderMap[o.stock.id] = o;
      });

      const toAdd = [];
      const toEdit = [];
      const toDelete = [];

      for (const o of existingOrders) {
        const match = selectedItems.find((i) => i.id === o.stock.id);
        if (match) {
          toEdit.push({ original: o, updated: match });
        } else {
          const stock = await o.stock.fetch();
          toDelete.push({ original: o, updated: stock });
        }
      }
      selectedItems.forEach((i) => {
        if (!orderMap[i.id]) toAdd.push(i);
      });

      if (toAdd.length) {
        const ordersByItemId = await bulkCreateOrdersV3(
          business,
          expense,
          toAdd,
          invoiceUpdatePayload.type,
          "quick_expense",
        );
        await bulkUpdateStockV2(toAdd, invoiceUpdatePayload.type);
        await bulkCreateInventoryV3(
          business,
          expense,
          toAdd,
          invoiceUpdatePayload.type,
          "cash",
          ordersByItemId,
        );
      }
      if (toEdit.length) {
        await bulkUpdateTransactionStock(toEdit, invoiceUpdatePayload.type);
        await bulkUpdateInventory(
          business,
          expense,
          toEdit,
          invoiceUpdatePayload.type,
          "cash",
        );
        await bulkUpdateOrders(toEdit);
      }
      if (toDelete.length) {
        await bulkRestoreStock(toDelete, invoiceUpdatePayload.type);
        await bulkDeleteOrders(toDelete);
        await bulkDeleteInventory(expense, toDelete, invoiceUpdatePayload.type, "cash");
      }
    }

    Sync(user?.phone_number, navigate);
  };

  const submitVerifyPIN = async () => {
    setError("");
    const pin = PIN;
    if (pin.length === 4) {
      try {
        setLoading(true);
        const response = await handleApi(
          CHECK_PIN_GET,
          { rnp_verify: true, pin },
          null,
          navigate,
          { "Web-Token": Cookies.get("deviceId") },
        );

        if (response.success) {
          try {
            const data = {
              amount: +amount,
              to_bank: accountDetail.bankName,
              to_iban: accountDetail?.iban,
              title: accountDetail?.accountTitle,
              idempotency_key: idempotencyKey,
              ...(categoryId && { category_id: categoryId }),
              ...(notes && { note: notes }),
            };
            triggerLogEvent(analytics, "w_send_sent");
            const response = await handleApi(
              WALLETSENTMONEY,
              data,
              undefined,
              navigate,
            );
            if (
              response?.status === "COMPLETED" &&
              accountDetail.bankName === "ucash"
            ) {
              const transaction = response?.transaction_details || {};
              const data = {
                from: `${
                  transaction?.sender_name
                }(****${transaction?.sender_account?.slice(-4)})`,
                amount: Math.abs(transaction?.amount),
                to: `${transaction?.account_title}(****${transaction?.account_number})`,
                bankLogo: transaction?.bank_logo_link,
                type: transaction?.type,
                date: transaction?.created_at,
              };
              if (timeoutId) {
                clearTimeout(timeoutId); // Clear the timeout if the request was successful
              }
              try {
                await createExpenseTransaction();
                await updateExistingExpense();
              } catch {}
              fetchWalletData();
              if (!openRef.current) return;
              navigate(location.pathname, { replace: true, state: {} });
              setLoading(false);
              navigate(`/UCash/TransactionSuccess/${response?.job_id}`, {
                state: { data, transaction, backPath },
              });
            } else fetchWalletSendmoneyTransaction(response?.job_id);
          } catch (error) {
            setLoading(false);
            showSnackbar({
              message:
                error?.response?.data?.message || I18n.error_in_send_money,
              severity: "error",
              icon: "",
            });
          }
        } else {
          setLoading(false);
          setPIN("");
          focusFirstInput();
          if (response.is_locked) {
            showSnackbar({
              message: I18n.your_account_has_been_locked,
              severity: "error",
              icon: "",
            });
          }
          if (response.message && response.attempts_left) {
            setError(
              `${response.message} (${response.attempts_left} ${I18n.tries_left})`,
            );
          }
        }
      } catch (error) {
        setPIN("");
        setLoading(false);
      }
    }
  };
  let timeoutId = null;

  const fetchWalletSendmoneyTransaction = async (id) => {
    try {
      const response = await handleApi(
        GETWALLETSENTMONEY,
        { job_id: id },
        undefined,
        navigate,
      );
      if (response?.status === "COMPLETED") {
        const transaction = response?.transaction_details || {};
        const data = {
          from: `${
            transaction?.sender_name
          }(****${transaction?.sender_account?.slice(-4)})`,
          amount: Math.abs(transaction?.amount),
          to: `${transaction?.account_title}(****${transaction?.account_number})`,
          bankLogo: transaction?.bank_logo_link,
          type: transaction?.type,
          date: transaction?.created_at,
        };
        if (timeoutId) {
          clearTimeout(timeoutId); // Clear the timeout if the request was successful
        }
        try {
          await createExpenseTransaction();
          await updateExistingExpense();
        } catch {}
        fetchWalletData();
        if (!openRef.current) return;
        navigate(location.pathname, { replace: true, state: {} });
        setLoading(false);
        navigate(`/UCash/TransactionSuccess/${id}`, {
          state: { data, transaction, backPath },
        });
      } else if (response?.status == "FAILED") {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        if (!openRef.current) return;
        setLoading(false);
        showSnackbar({
          message: response?.message || I18n.transaction_failed,
          severity: "error",
        });
      } else {
        setLoading(true);
        timeoutId = setTimeout(() => fetchWalletSendmoneyTransaction(id), 5000);
      }
    } catch (error) {
      if (!openRef.current) return;
      showSnackbar({
        message: error?.response?.data?.message || I18n.error_in_send_money,
        severity: "error",
        icon: "",
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setPIN("");
    }
  }, [open]);

  return (
    <>
      <UBAppModal
        id="verify_pin_modal"
        size="lg"
        className={modalStyles.verifyPinRoot}
        open={open}
        onClose={() => {
          if (!loading) handleClose();
        }}
        header={{
          title: (
            <ParagraphText className="d-flex align-items-center fontSize20 fontWeight500 newFontColor">
              {I18n.verify_PIN}
            </ParagraphText>
          ),
          handleClose,
          dont_show_cross_icon: true,
        }}
        footer={[
          {
            data: {
              onClick: handleClose,
              title: I18n.back,
              id: "verify_pin_back_btn",
              className: `ubSecondaryButton ${modalStyles.backBtn}`,
              disabled: loading,
            },
          },
          {
            data: {
              id: "verify_pin_confirm_btn",
              className: `ubPrimaryButton ${modalStyles.sendBtn}`,
              title: `${I18n.send} RS. ${formatNum(amount)}`,
              onClick: submitVerifyPIN,
              disabled: isDisabled,
            },
          },
        ]}
      >
        <Box
          className={modalStyles.body}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !isDisabled) {
              submitVerifyPIN();
            }
          }}
        >
          {loading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 10,
                backgroundColor: "var(--modalLoadingOverlay)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "8px",
              }}
            >
              <CircularProgress size={32} sx={{ color: "var(--mainColor)" }} />
            </Box>
          )}
          <div ref={otpContainerRef}>
            <UBOTPInput
              label={I18n.enter_pin}
              className={modalStyles.otpInput}
              value={PIN}
              onChangeQuery={onChange}
              shouldAutoFocus
              invalid={!!error}
              helperText={error}
              isInputSecure={true}
              id="verify_pin_input"
              disabled={loading}
            />
          </div>
          <ParagraphText
            className={`fontSize16 ${modalStyles.forgotPin}`}
            onClick={handleForgotPIN}
            id="verify_pin_modal_forgot_pin"
          >
            {I18n.forgot_pin}
          </ParagraphText>
        </Box>
      </UBAppModal>
    </>
  );
};

export default VerifyPinModal;
